import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  contentType,
  courseStatus,
  DOMAIN_LOCALES,
  EXAM_TYPE,
  TAB_STATUS,
} from '@shared/const';
import { logger, newSelectTranslation } from '@shared/utils';

import {
  COMPLETION_INTEGRITY_FEATURE_FLAG,
  useFeatureFlagEnabled,
} from '../../../config';
import { useAppGlobals } from '../AppContext';
import {
  getCourse,
  getLessonsProcessingStatus,
  validateGame,
  validateCatalogRemoval,
} from './queries';
import {
  deleteCourse,
  deletePublishedCourse,
  moodleSync,
  updateCourse,
} from './mutations';
import ERRORS from './errors';
import TABS from './tabs';
import validateCourse from './validate';

export default function useCourse({ navigate, courseId }) {
  const { t } = useTranslation();

  const [canPublish, setCanPublish] = useState(false);
  const [course, setCourse] = useState(null);
  const [currentTabValidation, setCurrentTabValidation] = useState({});
  const [errorMessage, setErrorMessage] = useState();
  const [errorType, setErrorType] = useState();
  const [detailedErrors, setDetailedErrors] = useState();
  const [hasError, setHasError] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [hasQuiz, setHasQuiz] = useState(false);
  const [handleSave, setHandleSave] = useState(null);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [validationDetails, setValidationDetails] = useState();
  const [selectedTab, setSelectedTab] = useState(TABS.DESCRIPTION);
  const [submittingRefresh, setSubmittingRefresh] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [saveAccessInput, setSaveAccessInput] = useState({});

  const {
    domain,
    locale,
    useLazyGraphQLQuery,
    useGraphQLMutation,
    videocationCatalog,
  } = useAppGlobals();

  const completionIntegrityFlagEnabled = useFeatureFlagEnabled(
    COMPLETION_INTEGRITY_FEATURE_FLAG,
    import.meta.env.VITE_COMPLETION_INTEGRITY_FLAG_OVERRIDE === 'true'
  );

  const [getCourseQuery, getCourseResponse] = useLazyGraphQLQuery(getCourse);
  const [validateGameQuery, validateGameResponse] = useLazyGraphQLQuery(
    validateGame,
    { fetchPolicy: 'no-cache' }
  );
  const [getLessonsProcessingStatusQuery, getLessonsProcessingStatusResponse] =
    useLazyGraphQLQuery(getLessonsProcessingStatus, {
      fetchPolicy: 'no-cache',
    });
  const [validateCatalogRemovalQuery, validateCatalogRemovalResponse] =
    useLazyGraphQLQuery(validateCatalogRemoval, {
      fetchPolicy: 'network-only',
    });

  const [updateCourseMutation, updateCourseResponse] = useGraphQLMutation(
    updateCourse,
    {
      onCompleted({ updateCourse }) {
        setCourse(updateCourse);
      },
      onError({ graphQLErrors, networkError, message }) {
        if (graphQLErrors) {
          setErrorMessage(graphQLErrors[0].message);
        }
        if (networkError) {
          setErrorMessage(networkError);
        }
        setErrorType(ERRORS.SAVE);
        setHasError(true);
        if (message) {
          setDetailedErrors([
            {
              title: t('backoffice.courses.errorSavingCourse'),
              description: message,
            },
          ]);
        }
      },
    }
  );

  const [deleteCourseMutation, deleteCourseResponse] = useGraphQLMutation(
    deleteCourse,
    {
      onCompleted() {
        const courseName = newSelectTranslation(course.name, locale, '');
        navigate(`/courses?deletedCourseName=${courseName}`);
      },
      onError({ errors }) {
        setErrorMessage(errors[0].message);
        setErrorType(ERRORS.DELETE);
        setHasError(true);
      },
    }
  );

  const [deletePublishedCourseMutation] = useGraphQLMutation(
    deletePublishedCourse,
    {
      onCompleted({ updateCourse } = {}) {
        setCourse({ ...course, ...updateCourse });
      },
      onError({ errors }) {
        logger.error('Error occured while deleting published course', errors);
        setErrorMessage(errors[0].message);
        setErrorType(ERRORS.DELETE_PUBLISHED);
        setHasError(true);
      },
    }
  );

  const [moodleSyncMutation, moodleSyncResponse] = useGraphQLMutation(
    moodleSync,
    {
      onCompleted({ moodleSync }) {
        setCourse((prevCourse) => ({
          ...prevCourse,
          moodleCourseUrl: moodleSync?.url || null,
        }));
      },
      onError({ graphQLErrors, networkError }) {
        if (graphQLErrors) {
          setErrorMessage(graphQLErrors[0].message);
        }
        if (networkError) {
          setErrorMessage(networkError);
        }
        setErrorType(ERRORS.CREATE_IN_GLUP);
        setHasError(true);
      },
    }
  );

  const submitting =
    deleteCourseResponse.loading ||
    updateCourseResponse.loading ||
    moodleSyncResponse.loading;

  const clearError = () => {
    setHasError(false);
    setErrorMessage();
    setErrorType();
    setDetailedErrors();
  };

  useEffect(() => {
    const { loading, data, error } = getCourseResponse;
    if (!loading && (data || error)) {
      if (error) {
        logger.error('Error occured while retrieving course', error);
        setHasError(true);
      } else if (data?.getCourse) {
        setCourse(data.getCourse);
        if (data.getCourse.status === courseStatus.PUBLISHED) setLocked(true);
      } else {
        navigate('/404');
      }
      setLoading(false);
    }
  }, [getCourseResponse]);

  useEffect(() => {
    getCourseQuery({
      variables: {
        id: courseId,
      },
    });
  }, [courseId]);

  useEffect(() => {
    if (course) {
      if (course.exam) {
        setHasQuiz(true);
      } else {
        setHasQuiz(false);
      }

      const languages = DOMAIN_LOCALES[domain];

      const originalWithoutSubtitles =
        course.status === courseStatus.PUBLISHED ||
        course.status === courseStatus.ARCHIVED
          ? course.episodes?.every((episode) => {
              const { contentType: type, content } = episode;
              return (
                type === contentType.GAME || content?.subtitles?.length === 0
              );
            })
          : undefined;

      const validationStatus = validateCourse(
        course,
        languages,
        originalWithoutSubtitles,
        videocationCatalog
      );

      setCanPublish(Object.keys(validationStatus).length === 0);
      setValidationDetails(validationStatus);
    }
  }, [course, videocationCatalog]);

  const getLanguageStatus = (language, courseProp = course) => {
    const isPublished = courseProp?.status === courseStatus.PUBLISHED;
    const languages = DOMAIN_LOCALES[domain];

    const hasDescription =
      courseProp?.description &&
      !!newSelectTranslation(courseProp.description, language, '');
    const hasName =
      courseProp?.name && !!newSelectTranslation(courseProp.name, language, '');
    const hasShortDescription =
      courseProp?.shortDescription &&
      !!newSelectTranslation(courseProp.shortDescription, language, '');
    const hasLearningPoints =
      courseProp?.learningPoints &&
      !!newSelectTranslation(courseProp.learningPoints, language, []).length >
        0;

    const isCompleted =
      hasDescription && hasName && hasShortDescription && hasLearningPoints;
    const isInProgress =
      (languages || [])[language] ||
      hasDescription ||
      hasName ||
      hasShortDescription ||
      hasLearningPoints;

    if (isPublished && locked && (isCompleted || isInProgress)) return '';
    if (isCompleted) return TAB_STATUS.COMPLETED;
    if (isInProgress) return TAB_STATUS.IN_PROGRESS;
    return TAB_STATUS.DISABLED;
  };

  /*
   * To be able to reuse the function with the original course, or the modified one,
   * I added the courseProp param, which defaults to the original course
   */
  const getLanguages = () => {
    const locales = DOMAIN_LOCALES[domain];
    return locales.reduce(
      (acc, language) => ({ ...acc, [language]: true }),
      {}
    );
  };

  const languageMap = getLanguages();
  const enabledLanguages = Object.keys(languageMap).filter(
    (lang) => languageMap[lang]
  );

  const saveExam = (exam) => {
    const examInput = {};

    if (exam.type === EXAM_TYPE.NONE) {
      examInput.delete = true;
    } else {
      examInput.create = {
        type: exam.type,
      };

      if (exam.type === EXAM_TYPE.GAME) {
        examInput.create.game = {
          externalGameId: exam.externalGameId,
        };
      } else {
        examInput.create.test = {
          questions: exam.questions,
        };
      }
    }

    updateCourseMutation({
      variables: {
        input: {
          id: course.id,
          exam: examInput,
        },
      },
    });
  };

  const deleteCourseHandler = (course) => {
    clearError();
    deleteCourseMutation({
      variables: {
        id: course.id,
      },
    });
  };

  const saveAttachments = ({ attachments }) => {
    const attachmentsToBeCreated = (attachments || []).reduce((acc, curr) => {
      if (!curr.id) {
        acc.push({
          name: curr.name,
          file: curr.file.key,
        });
      }
      return acc;
    }, []);
    const attachmentsToBeUpdated = (attachments || []).reduce((acc, curr) => {
      if (curr.updated && curr.id) {
        const originalAttachment = (course.attachments || []).find(
          (attachment) => attachment.id === curr.id
        );

        if (originalAttachment) {
          const originalNamesByLang = new Map(
            (originalAttachment.name ?? []).map((n) => [
              n.languageCode,
              n.value,
            ])
          );
          const changedNames = curr.name.filter(
            ({ languageCode, value }) =>
              originalNamesByLang.get(languageCode) !== value
          );

          if (changedNames.length > 0) {
            acc.push({
              id: curr.id,
              name: changedNames,
            });
          }
        }
      }
      return acc;
    }, []);
    const updatedIds = (attachments || [])
      .map((attachment) => attachment.id)
      .filter(Boolean);
    const attachmentsToBeDeleted = (course.attachments || []).reduce(
      (acc, curr) => {
        if (!updatedIds.includes(curr.id)) {
          acc.push(curr.id);
        }
        return acc;
      },
      []
    );

    clearError();
    updateCourseMutation({
      variables: {
        input: {
          id: course.id,
          attachments: {
            create: attachmentsToBeCreated.length
              ? attachmentsToBeCreated
              : undefined,
            update: attachmentsToBeUpdated.length
              ? attachmentsToBeUpdated
              : undefined,
            delete: attachmentsToBeDeleted.length
              ? attachmentsToBeDeleted
              : undefined,
          },
        },
      },
      onCompleted() {
        setHasChanged(false);
      },
    });
  };

  const clearMoodleCourseUrl = () => {
    clearError();
    updateCourseMutation({
      variables: {
        input: {
          id: course.id,
          moodleCourseUrl: null,
        },
      },
    });
  };

  const syncMoodleCourse = () => {
    clearError();
    moodleSyncMutation({
      variables: {
        input: {
          courseId: course.id,
        },
      },
    });
  };

  const saveDescription = (values) => {
    const descriptionInput = {
      ...values,
      id: course.id,
    };

    const fillFromNameOrEmpty = () =>
      Array.isArray(values.name)
        ? values.name.map((t) => ({ ...t, value: '' }))
        : [];

    const stripNullTranslationValues = (arr) => {
      if (arr == null) {
        return null;
      }
      const cleaned = arr.filter(
        (t) => t?.value != null && String(t.value).trim() !== ''
      );
      return cleaned.length ? cleaned : null;
    };

    if (values.level) {
      descriptionInput.prerequisites = stripNullTranslationValues(
        values.prerequisites === null
          ? fillFromNameOrEmpty()
          : values.prerequisites
      );
      descriptionInput.typicalLearner = stripNullTranslationValues(
        values.typicalLearner === null
          ? fillFromNameOrEmpty()
          : values.typicalLearner
      );
    } else {
      descriptionInput.prerequisites = stripNullTranslationValues(
        values.prerequisites
      );
      descriptionInput.typicalLearner = stripNullTranslationValues(
        values.typicalLearner
      );
    }

    clearError();
    updateCourseMutation({
      variables: {
        input: descriptionInput,
      },
    });
  };

  const saveAccess = ({ type, catalogs, integrity, examTimeLimitSeconds }) => {
    const originalCatalogs = course.catalogs?.map((cat) => cat.id);
    const removedCatalogs = originalCatalogs.filter(
      (cat) => !catalogs.includes(cat)
    );
    clearError();
    const input = { id: course.id, type, catalogs };
    // integrity/examTimeLimitSeconds are only sent when the completion
    // integrity flag is enabled; omitting both leaves the stored values
    // unchanged (backend only touches these columns when at least one key
    // is present on the input).
    if (completionIntegrityFlagEnabled) {
      input.integrity = integrity;
      // examTimeLimitSeconds is only defined when integrity is strict; when
      // omitted the backend coerces the stored value to 0.
      if (examTimeLimitSeconds !== undefined) {
        input.examTimeLimitSeconds = examTimeLimitSeconds;
      }
    }
    if (removedCatalogs?.length) {
      // Persist via the deferred path resolved after catalog-removal
      // validation; the same input carries integrity/examTimeLimitSeconds.
      setSaveAccessInput(input);
      validateCatalogRemovalQuery({
        variables: {
          input: {
            courseId: course.id,
            catalogIds: removedCatalogs,
          },
        },
      });
    } else {
      updateCourseMutation({
        variables: { input },
      });
    }
  };

  const saveLessons = (newCourse) => {
    clearError();

    const input = { id: newCourse.id };

    // handle trailer video
    if (newCourse.trailerVideo) {
      input.trailer = {
        file: {
          updateFile: newCourse.trailerVideo.key,
        },
      };
    } else if ('trailerVideo' in newCourse) {
      input.trailer = {
        file: {
          deleteFile: true,
        },
      };
    }

    // handle trailer subtitles
    const trailerSubtitlesToCreate = (newCourse.trailerSubtitles || [])
      .filter(
        (subtitle) =>
          !(course.trailer?.subtitles || []).find(
            (originalSubtitle) =>
              subtitle.language.code === originalSubtitle.language.code
          )
      )
      .map((subtitle) => ({
        file: subtitle.file.key,
        languageCode: subtitle.language.code,
      }));
    const trailerSubtitlesToUpdate = (newCourse.trailerSubtitles || []).reduce(
      (subtitles, subtitle) => {
        const originalSubtitle = (course.trailer?.subtitles || []).find(
          (originalSubtitle) =>
            originalSubtitle.language.code === subtitle.language.code
        );
        if (!originalSubtitle) {
          return subtitles;
        }

        if (subtitle.file?.key) {
          subtitles.push({
            languageCode: subtitle.language.code,
            file: subtitle.file.key,
          });
        }

        return subtitles;
      },
      []
    );
    const trailerSubtitlesToDelete = (course.trailer?.subtitles || [])
      .filter(
        (originalSubtitle) =>
          !(newCourse.trailerSubtitles || []).find(
            (subtitle) =>
              originalSubtitle.language.code === subtitle.language.code
          )
      )
      .map((subtitle) => ({ languageCode: subtitle.language.code }));

    if (
      trailerSubtitlesToCreate.length ||
      trailerSubtitlesToUpdate.length ||
      trailerSubtitlesToDelete.length
    ) {
      input.trailer = input.trailer || {};
      input.trailer.subtitles = {};

      if (trailerSubtitlesToCreate.length) {
        input.trailer.subtitles.create = trailerSubtitlesToCreate;
      }
      if (trailerSubtitlesToUpdate.length) {
        input.trailer.subtitles.update = trailerSubtitlesToUpdate;
      }
      if (trailerSubtitlesToDelete.length) {
        input.trailer.subtitles.delete = trailerSubtitlesToDelete;
      }
    }

    // handle episodes
    const episodesToCreate = newCourse.episodes.filter(
      (episode) => !episode.id
    );
    const episodesToUpdate = newCourse.episodes.filter(
      (episode) => episode.id && Object.keys(episode).length > 1
    );
    const episodesToDelete = course.episodes.filter(
      (originalEpisode) =>
        !newCourse.episodes.find((episode) => episode.id === originalEpisode.id)
    );

    if (
      episodesToCreate.length ||
      episodesToUpdate.length ||
      episodesToDelete.length
    ) {
      input.episodes = {};

      // handle create episodes
      if (episodesToCreate.length) {
        input.episodes.create = episodesToCreate.map((episode) => {
          const episodeParsed = {
            name: episode.name,
            ordinal: episode.ordinal,
          };

          const hasAttachments = episode.attachments?.length;
          const hasSubtitles = episode.subtitles?.length;
          const hasVideo = episode.video;

          if (hasSubtitles || hasVideo) {
            episodeParsed.video = {};

            // handle create episode subtitle
            if (hasSubtitles) {
              episodeParsed.video.subtitles = episode.subtitles.map(
                (subtitle) => ({
                  languageCode: subtitle.language.code,
                  file: subtitle.file.key,
                })
              );
            }

            // handle create episode video
            if (hasVideo) {
              episodeParsed.video.file = episode.video.key;
            }
          }

          // handle create episode attachments
          if (hasAttachments) {
            episodeParsed.attachments = episode.attachments.map(
              (attachment) => ({
                file: attachment.file.key,
                name: attachment.name,
              })
            );
          }

          if (episode.game) {
            episodeParsed.game = episode.game;
          }

          return episodeParsed;
        });
      }

      // handle update episodes
      if (episodesToUpdate.length) {
        input.episodes.update = episodesToUpdate.map((episode) => {
          const originalEpisode = course.episodes.find(
            (originalEpisode) => originalEpisode.id === episode.id
          );

          const episodeParsed = { id: episode.id };

          // handle update episode name
          const changedNames = episode.name.reduce((names, name) => {
            const originalName = (originalEpisode.name || []).find(
              (originalName) => originalName.languageCode === name.languageCode
            );
            if (originalName?.value !== name.value || !originalName) {
              names.push(name);
            }
            return names;
          }, []);

          if (changedNames.length) {
            episodeParsed.name = changedNames;
          }

          // handle update episode ordinal
          if (episode.ordinal !== originalEpisode.ordinal) {
            episodeParsed.ordinal = episode.ordinal;
          }

          if (originalEpisode.contentType === contentType.GAME) {
            episodeParsed.game = episode.game;
          }

          // handle update episode video variables
          const hasOriginalVideo =
            originalEpisode?.content?.file?.processingStatus;
          const hasVideo =
            !episode.game && (episode.video === null || episode.video);

          // handle update episode subtitle variables
          const subtitlesToCreate = (episode.subtitles || [])
            .filter(
              (subtitle) =>
                !(originalEpisode?.content?.subtitles || []).find(
                  (originalSubtitle) =>
                    subtitle.language.code === originalSubtitle.language.code
                )
            )
            .map((subtitle) => ({
              file: subtitle.file.key,
              languageCode: subtitle.language.code,
            }));
          const subtitlesToUpdate = (episode.subtitles || []).reduce(
            (subtitles, subtitle) => {
              const originalSubtitle = (
                originalEpisode?.content?.subtitles || []
              ).find(
                (originalSubtitle) =>
                  originalSubtitle.language.code === subtitle.language.code
              );
              if (!originalSubtitle) {
                return subtitles;
              }

              if (subtitle.file?.key) {
                subtitles.push({
                  languageCode: subtitle.language.code,
                  file: subtitle.file.key,
                });
              }

              return subtitles;
            },
            []
          );
          const subtitlesToDelete = (originalEpisode?.content?.subtitles || [])
            .filter(
              (originalSubtitle) =>
                !(episode.subtitles || []).find(
                  (subtitle) =>
                    originalSubtitle.language.code === subtitle.language.code
                )
            )
            .map((subtitle) => ({ languageCode: subtitle.language.code }));

          // handle update episode video key
          if (
            (!hasVideo && hasOriginalVideo) ||
            hasVideo ||
            subtitlesToCreate.length ||
            subtitlesToUpdate.length ||
            subtitlesToDelete.length
          ) {
            episodeParsed.video = {};

            // handle update episodes video
            if ((!hasVideo && hasOriginalVideo) || hasVideo) {
              episodeParsed.video.file = {};
              if (episode?.video?.key) {
                episodeParsed.video.file.updateFile = episode.video.key;
              } else if (episode?.video === null) {
                episodeParsed.video.file.deleteFile = true;
              }
            }

            // handle update episode subtitles
            if (
              subtitlesToCreate.length ||
              subtitlesToUpdate.length ||
              subtitlesToDelete.length
            ) {
              episodeParsed.video.subtitles = {};

              if (subtitlesToCreate.length) {
                episodeParsed.video.subtitles.create = subtitlesToCreate;
              }
              if (subtitlesToUpdate.length) {
                episodeParsed.video.subtitles.update = subtitlesToUpdate;
              }
              if (subtitlesToDelete.length) {
                episodeParsed.video.subtitles.delete = subtitlesToDelete;
              }
            }
          }

          // handle update episode attachment variables
          const attachmentsToCreate = (episode.attachments?.create || [])
            .filter((attachment) => !attachment.id)
            .map((attachment) => ({
              file: attachment.file.key,
              name: attachment.name,
            }));
          const attachmentsToUpdate = (
            episode.attachments?.update || []
          ).reduce((attachments, attachment) => {
            if (!attachment.id) {
              return attachments;
            }
            const result = { id: attachment.id };
            if (attachment.file?.key) {
              result.file = attachment.file?.key;
            }

            const names = attachment.name.filter((name) => {
              const originalName = (originalEpisode.attachments || [])
                .find(
                  (originalAttachment) =>
                    originalAttachment.id === attachment.id
                )
                ?.name?.find(
                  (originalName) =>
                    originalName.languageCode === name.languageCode
                );
              return !originalName || originalName.value !== name.value;
            });

            if (names.length) {
              result.name = names;
            }

            if (Object.keys(result).length) {
              attachments.push(result);
            }

            return attachments;
          }, []);
          const attachmentsToDelete = originalEpisode.attachments
            .filter((originalAttachment) =>
              (episode.attachments?.delete || []).find(
                (attachment) => originalAttachment.id === attachment
              )
            )
            .map((attachment) => attachment.id);

          // handle update episode attachments
          if (
            attachmentsToCreate.length ||
            attachmentsToUpdate.length ||
            attachmentsToDelete.length
          ) {
            episodeParsed.attachments = {};

            if (attachmentsToCreate.length) {
              episodeParsed.attachments.create = attachmentsToCreate;
            }
            if (attachmentsToUpdate.length) {
              episodeParsed.attachments.update = attachmentsToUpdate;
            }
            if (attachmentsToDelete.length) {
              episodeParsed.attachments.delete = attachmentsToDelete;
            }
          }

          return episodeParsed;
        });
      }

      // handle delete episode
      if (episodesToDelete.length) {
        input.episodes.delete = episodesToDelete.map((episode) => episode.id);
      }
    }

    updateCourseMutation({
      variables: { input },
      update(cache) {
        episodesToDelete.forEach((episode) => {
          cache.evict({
            id: cache.identify(episode),
          });
        });
        cache.gc();
      },
    });
  };

  const saveDisplay = (display) => {
    clearError();

    const { categories, experts, thumbnail, promoPicture } = display;

    const input = {
      id: course.id,
    };

    if (categories?.length || course.categories.length) {
      input.categories = categories?.map(({ id }) => id) || [];
    }

    if (experts?.length || course.experts.length) {
      input.experts = experts?.map(({ id }) => id) || [];
    }

    if (thumbnail?.file?.key) {
      input.thumbnail = {
        updateFile: thumbnail.file.key.key,
      };
    } else if (course.thumbnail && !thumbnail) {
      input.thumbnail = { deleteFile: true };
    }

    if (promoPicture?.file?.key) {
      input.promoPicture = {
        updateFile: promoPicture.file.key.key,
      };
    } else if (course.promoPicture && !promoPicture) {
      input.promoPicture = { deleteFile: true };
    }

    updateCourseMutation({ variables: { input } });
  };

  const publish = (newCourse) => {
    clearError();
    updateCourseMutation({
      variables: {
        input: {
          id: newCourse.id,
          status: courseStatus.PUBLISHED,
        },
      },
    });
  };

  const getFormattedTranslations = (content) => {
    let formattedField = {};
    try {
      formattedField = JSON.parse(content || '{}');
    } catch (err) {
      logger.error('Error: ', err);
    }
    return formattedField;
  };

  const publishDraftEpisodes = () => {
    clearError();
    updateCourseMutation({
      variables: {
        input: {
          id: course.id,
          publishDraftEpisodes: true,
        },
      },
    });
  };

  const deletePublishedCourseHandler = () => {
    clearError();

    deletePublishedCourseMutation({
      variables: {
        input: {
          id: course.id,
          status: courseStatus.ARCHIVED,
        },
      },
    });
  };

  // Fetch and update the processing status of any lesson items (videofiles, attachments and subtitles) and the trailer
  const fetchLessonsProcessingStatus = async () => {
    setSubmittingRefresh(true);

    getLessonsProcessingStatusQuery({
      variables: {
        id: courseId,
      },
    });
  };

  useEffect(() => {
    const { data, loading, error } = getLessonsProcessingStatusResponse;

    if (!loading && (data || error)) {
      if (error) {
        logger.error('Error occured while retrieving course', error);
        setHasError(true);
      } else if (data?.getCourse) {
        const processingData = data.getCourse;
        setCourse({
          ...course,
          ...(processingData.trailer?.file?.processingStatus ||
          processingData.trailer?.subtitles?.length
            ? {
                trailer: {
                  ...course.trailer,
                  ...(processingData.trailer?.file?.processingStatus
                    ? {
                        file: {
                          ...course.trailer.file,
                          processingStatus:
                            processingData.trailer.file.processingStatus,
                        },
                      }
                    : {}),
                  ...(processingData.trailer?.subtitles?.length
                    ? {
                        subtitles: (course.trailer?.subtitles || []).map(
                          (subtitle) => {
                            const subtitleProcessingData =
                              processingData.trailer.subtitles.find(
                                ({ language }) =>
                                  language?.code === subtitle.language?.code
                              );
                            return {
                              ...subtitle,
                              file: {
                                processingStatus:
                                  subtitleProcessingData?.file
                                    ?.processingStatus,
                              },
                            };
                          }
                        ),
                      }
                    : {}),
                },
              }
            : {}),
          episodes: course.episodes.map((episode) => {
            const episodeProcessingData = processingData.episodes.find(
              ({ id }) => id === episode.id
            );
            return {
              ...episode,
              ...(episodeProcessingData.content?.subtitles?.length ||
              episodeProcessingData.content?.file?.processingStatus
                ? {
                    content: {
                      ...episode.content,
                      ...(episodeProcessingData.content?.file?.processingStatus
                        ? {
                            file: {
                              ...episode.content.file,
                              processingStatus:
                                episodeProcessingData.content.file
                                  .processingStatus,
                            },
                          }
                        : {}),
                      ...(episodeProcessingData.content?.subtitles?.length
                        ? {
                            subtitles: (episode.content?.subtitles || []).map(
                              (subtitle) => {
                                const subtitleProcessingData =
                                  episodeProcessingData.content.subtitles.find(
                                    ({ language }) =>
                                      language?.code === subtitle.language?.code
                                  );
                                return {
                                  ...subtitle,
                                  file: {
                                    processingStatus:
                                      subtitleProcessingData?.file
                                        ?.processingStatus,
                                  },
                                };
                              }
                            ),
                          }
                        : {}),
                    },
                  }
                : {}),
              attachments: episode.attachments.map((attachment) => {
                const attachmentProcessingData =
                  episodeProcessingData.attachments.find(
                    ({ id }) => id === attachment.id
                  );
                return {
                  ...attachment,
                  processingStatus:
                    attachmentProcessingData?.file?.processingStatus,
                };
              }),
            };
          }),
        });
      }
      setSubmittingRefresh(false);
    }
  }, [getLessonsProcessingStatusResponse]);

  useEffect(() => {
    const handleResponse = () => {
      const { data, loading, error } = validateCatalogRemovalResponse;
      if (!loading && data && !error) {
        const {
          allowed,
          isGloballyFeatured,
          usedInTemplates,
          organizationErrors,
        } = data.validateCatalogRemoval;
        if (allowed) {
          updateCourseMutation({
            variables: { input: saveAccessInput },
          });
          return;
        }
        setSaveAccessInput();
        const details = [];
        if (isGloballyFeatured) {
          details.push({
            title: t('backoffice.courses.accessTab.globallyFeatured'),
            description: t(
              'backoffice.courses.accessTab.globallyFeaturedDescription'
            ),
          });
        }
        if (usedInTemplates.length) {
          details.push({
            title: t('backoffice.courses.accessTab.usedInTemplates'),
            description: usedInTemplates
              .map(
                (template) => `"${newSelectTranslation(template.name, locale)}"`
              )
              .join(', '),
          });
        }
        if (organizationErrors.length) {
          organizationErrors.forEach(
            ({ organization, effectedPlaylists, effectedFeatured }) => {
              const organizationDetails = [];
              if (effectedFeatured) {
                organizationDetails.push(
                  t('backoffice.courses.accessTab.effectedFeatured')
                );
              }
              if (effectedPlaylists.length) {
                organizationDetails.push(
                  `${t('backoffice.courses.accessTab.effectedPlaylists')}: ${effectedPlaylists
                    .map(
                      (playlist) =>
                        `"${newSelectTranslation(playlist.name, locale)}"`
                    )
                    .join(', ')}`
                );
              }
              details.push({
                title: t('backoffice.courses.accessTab.organizationError', {
                  name: organization.name,
                }),
                description: organizationDetails.join(' | '),
              });
            }
          );
        }
        setDetailedErrors(details);
        setErrorType(ERRORS.VALIDATE_CATALOGS);
        setHasError(true);
      }
    };
    handleResponse();
  }, [validateCatalogRemovalResponse, saveAccessInput]);

  return {
    canPublish,
    clearError,
    clearMoodleCourseUrl,
    completionIntegrityFlagEnabled,
    course,
    currentTabValidation,
    deleteCourse: deleteCourseHandler,
    deletePublishedCourse: deletePublishedCourseHandler,
    detailedErrors,
    enabledLanguages,
    errorMessage,
    errorType,
    fetchLessonsProcessingStatus,
    getFormattedTranslations,
    validateGameQuery,
    validateGameResponse,
    getLanguageStatus,
    getLanguages,
    handleSave,
    hasChanged,
    hasError,
    hasQuiz,
    loading,
    locked,
    publish,
    publishDraftEpisodes,
    saveAccess,
    saveAttachments,
    saveDescription,
    saveDisplay,
    saveExam,
    saveLessons,
    selectedTab,
    setCurrentTabValidation,
    setHandleSave,
    setHasChanged,
    setHasError,
    setLocked,
    setSelectedTab,
    setUploadingFiles,
    submitting,
    submittingRefresh,
    syncMoodleCourse,
    uploadingFiles,
    validateCourse,
    validationDetails,
  };
}
